import React, { useRef, useState } from 'react';

// CSS
import 'leaflet/dist/leaflet.css';
import './SessionDisplay.css';

// --- Import des composants enfants et du nouveau hook ---
import VideoDisplay from './VideoDisplay.jsx';
import AudioMapDisplay from './AudioMapDisplay.jsx';
import GpsMapDisplay from './GpsMapDisplay.jsx';
import { useSessionLogic, SESSION_EXPIRED } from '../hooks/useSessionLogic.js';
import ExpiredSessionNotice from './ExpiredSessionNotice.jsx';

const SessionDisplay = () => {
    const [alertDetails, setAlertDetails] = useState({ message: "Session en cours de préparation...", color: 'black' });
    const [address, setAddress] = useState('Recherche de l\'adresse...');
    const [showWelcome, setShowWelcome] = useState(true); // Popup d'accueil
    const [viewState, setViewState] = useState({
        videoSection: false,
        audioMapSection: false,
        gpsMapSection: false,
        audioControls: false,
        toggleVideoVisible: false,
        toggleAudioVisible: false,
        toggleText: ''
    });

    const refs = {
        video: useRef(null), canvas: useRef(null), canvasWrapper: useRef(null),
        mapElement: useRef(null), videoDisplaySection: useRef(null), audioMapDisplaySection: useRef(null),
        gpsMapDisplaySection: useRef(null), videoToggleButtonPlaceholder: useRef(null),
        audioToggleButtonPlaceholder: useRef(null), audioControlsToggleContainer: useRef(null),
        toggleViewButton: useRef(null), addressDisplayAudio: useRef(null), addressDisplayGps: useRef(null),
        mapContainerAudio: useRef(null), mapContainerGps: useRef(null),
        statusMessage: useRef(null)
    };
    const { timelineState, slaveState, rotationMode, toggleRotationMode, statusMessage, accessMessage, sessionState, isHistoricalMode, canGoLive, mediaHandlers, isDualCameraAvailable, selectedCamera, switchCamera } = useSessionLogic({ refs, setAlertDetails, setAddress, setViewState });

    if (accessMessage === SESSION_EXPIRED) {
        const apiUrl = window.location.hostname === 'www.monalerte.app' ? 'https://api.monalerte.app' : window.location.origin;
        return <ExpiredSessionNotice sessionId={window.location.pathname.split('/')[2]} apiUrl={apiUrl} />;
    }

    if (accessMessage) {
        return (
            <main className="session-container" style={{ display: 'grid', placeItems: 'center', minHeight: '100vh', padding: '24px' }}>
                <p style={{ color: '#E0E0E0', fontWeight: 'bold', textAlign: 'center' }}>{accessMessage}</p>
            </main>
        );
    }

    // Quand l'utilisateur clique "Continuer" → user gesture → autoplay autorisé → Go Live
    const handleWelcomeDismiss = () => {
        setShowWelcome(false);
        // Le clic de l'utilisateur est un "user gesture" → le navigateur autorise le play()
        mediaHandlers?.onGoLive?.();
    };

    // Historique : la lecture démarre seule au chargement, sans clic "Continuer".
    // Ce clic ne fait qu'activer le son (geste utilisateur requis par le
    // navigateur) — la lecture, elle, tourne déjà.
    const handleHistoricalWelcomeDismiss = () => {
        setShowWelcome(false);
        mediaHandlers?.onEnableSound?.();
    };

    return (
        <div className="session-container" style={{ flexDirection: 'column', alignItems: 'stretch' }}>

            {/* Popup d'accueil — nécessaire pour l'user gesture (politique autoplay navigateur) */}
            {showWelcome && canGoLive === true && (
                <div className="welcome-overlay" id="welcome-overlay">
                    <div className="welcome-card">
                        <div className="welcome-icon">🛡️</div>
                        <h2 className="welcome-title">Suivi en cours</h2>
                        <p className="welcome-text">
                            Vous êtes sur le point de suivre un partage de position en temps réel.
                        </p>
                        <p className="welcome-subtext">
                            La carte, l'audio et la vidéo seront synchronisés automatiquement.
                        </p>
                        <button
                            id="welcome-continue-btn"
                            className="welcome-button"
                            onClick={handleWelcomeDismiss}
                        >
                            ▶ Continuer
                        </button>
                    </div>
                </div>
            )}

            {/* Historique : lecture déjà en cours, ce clic active juste le son */}
            {showWelcome && canGoLive === false && (
                <div className="welcome-overlay" id="welcome-overlay">
                    <div className="welcome-card">
                        <div className="welcome-icon">🛡️</div>
                        <h2 className="welcome-title">Historique de la session</h2>
                        <p className="welcome-text">
                            Vous êtes sur le point de consulter l'historique de ce partage de position.
                        </p>
                        <p className="welcome-subtext">
                            La carte, l'audio et la vidéo seront synchronisés automatiquement.
                        </p>
                        <button
                            id="welcome-historical-continue-btn"
                            className="welcome-button"
                            onClick={handleHistoricalWelcomeDismiss}
                        >
                            ▶ Continuer
                        </button>
                    </div>
                </div>
            )}

            {alertDetails.message && (
                <div style={{
                    width: '100%',
                    padding: '12px 0',
                    backgroundColor: '#112240',
                    color: alertDetails.color === 'black' ? '#E0E0E0' : alertDetails.color,
                    textAlign: 'center',
                    fontWeight: 'bold',
                    fontSize: '1.1rem',
                    flexShrink: 0,
                    borderBottom: '1px solid rgba(255,255,255,0.08)',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                }}>
                    {alertDetails.message}
                </div>
            )}

            <div style={{ position: 'relative', width: '100%', flexGrow: 1, minHeight: 0, overflow: 'hidden' }}>
                <VideoDisplay
                    refs={refs}
                    viewState={viewState}
                    timelineState={timelineState}
                    slaveState={slaveState}
                    mediaHandlers={mediaHandlers}
                    canGoLive={canGoLive}
                    statusMessage={statusMessage}
                    rotationMode={rotationMode}
                    toggleRotationMode={toggleRotationMode}
                    isDualCameraAvailable={isDualCameraAvailable}
                    selectedCamera={selectedCamera}
                    onSwitchCamera={switchCamera}
                />
                <AudioMapDisplay
                    refs={refs}
                    address={address}
                    viewState={viewState}
                    timelineState={timelineState}
                    slaveState={slaveState}
                    mediaHandlers={mediaHandlers}
                    sessionData={sessionState}
                    isHistoricalMode={isHistoricalMode}
                    canGoLive={canGoLive}
                    rotationMode={rotationMode}
                    toggleRotationMode={toggleRotationMode}
                />
                <GpsMapDisplay
                    refs={refs}
                    address={address}
                    viewState={viewState}
                    timelineState={timelineState}
                    mediaHandlers={mediaHandlers}
                    sessionData={sessionState}
                    isHistoricalMode={isHistoricalMode}
                    canGoLive={canGoLive}
                />

                <div id="map" ref={refs.mapElement} style={{ width: '100%', height: '100%', position: 'absolute', top: '-9999px', left: '0px' }}></div>
                {/* Hidden native button to preserve useSessionLogic native bindings */}
                <button id="toggle-view" ref={refs.toggleViewButton} className="toggle-view-button" style={{ display: 'none' }}></button>
            </div>
        </div>
    );
};

export default SessionDisplay;
